import {
	clearKeycloakSessionStorage,
	configureKeycloak,
	consumeLastKeycloakOAuthError,
	formatKeycloakOAuthError,
	getKeycloak,
	getKeycloakDefaultScope,
	handleKeycloakNativeLaunchUrl,
	hasOAuthCallbackInCurrentUrl,
	initKeycloak,
	isOtpApiNativeAuthFlow,
	loginWithKeycloakNative,
	loginWithOtpApi,
	persistKeycloakTokens,
	redirectOAuthCallbackToLoginPath,
	registerOAuthBrowserClosedFallback,
	registerKeycloakNativeAuthListener,
	registerNativeAuthResumeGuard,
	startKeycloakTokenRefresh,
} from '@apexx/auth';
import { Capacitor } from '@capacitor/core';

let nativeLoginFailedCallback: (() => void) | undefined;
let authenticatedHandler: ((token: string) => Promise<void>) | undefined;
let sessionExpiredHandler: (() => Promise<void>) | undefined;

/** Allows the UI to subscribe to the "browser closed without deep link" event. */
export function onNativeLoginFailed(cb: () => void): void {
	nativeLoginFailedCallback = cb;
}

/** True when `VITE_NATIVE_AUTH_FLOW=otp-api` (works in web and native). */
export function shouldUseNativeOtpApiLogin(): boolean {
	return isOtpApiNativeAuthFlow();
}

export function setupKeycloakAuth(): void {
	configureKeycloak({
		url: import.meta.env.VITE_KEYCLOAK_URL,
		realm: import.meta.env.VITE_KEYCLOAK_REALM,
		clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
		appUrlScheme: import.meta.env.VITE_APP_URL_SCHEME,
		hubUrl: import.meta.env.VITE_HUB_URL,
		callbackPath: '/login',
		storageKey: 'apexx.hub.keycloak.session',
	});
}

async function applyAuthenticatedSession(
	token: string,
	onAuthenticated: (token: string) => Promise<void>,
	onSessionExpired: () => Promise<void>,
): Promise<void> {
	persistKeycloakTokens(getKeycloak());
	startKeycloakTokenRefresh({
		onTokenUpdated: (updatedToken) => {
			void onAuthenticated(updatedToken);
		},
		onSessionExpired: () => {
			void onSessionExpired();
		},
	});
	await onAuthenticated(token);
}

function logOAuthFailure(oauthError: ReturnType<typeof consumeLastKeycloakOAuthError>): void {
	if (oauthError) {
		console.error('Hub sign-in failed:', formatKeycloakOAuthError(oauthError));
	}
}

export async function bootstrapKeycloakAuth(
	onAuthenticated: (token: string) => Promise<void>,
	onUnauthenticated: () => Promise<void>,
	onSessionExpired: () => Promise<void> = onUnauthenticated,
	onNativeLogoutReturn?: () => Promise<void>,
): Promise<void> {
	authenticatedHandler = onAuthenticated;
	sessionExpiredHandler = onSessionExpired;

	if (redirectOAuthCallbackToLoginPath()) {
		return;
	}

	if (Capacitor.isNativePlatform()) {
		const deepLinkHandler = async (result: Awaited<ReturnType<typeof initKeycloak>> & { logoutReturn?: boolean }) => {
			if (result.logoutReturn) {
				await onNativeLogoutReturn?.();
				return;
			}

			if (result.authenticated && result.token) {
				await applyAuthenticatedSession(result.token, onAuthenticated, onSessionExpired);
				return;
			}

			logOAuthFailure(result.oauthError ?? consumeLastKeycloakOAuthError());
			await onUnauthenticated();
		};

		registerKeycloakNativeAuthListener(deepLinkHandler);
		registerOAuthBrowserClosedFallback(deepLinkHandler, () => {
			nativeLoginFailedCallback?.();
		});
		registerNativeAuthResumeGuard();
	}

	const launchResult = await handleKeycloakNativeLaunchUrl();
	if (launchResult?.logoutReturn) {
		await onNativeLogoutReturn?.();
		return;
	}

	if (launchResult?.authenticated && launchResult.token) {
		await applyAuthenticatedSession(launchResult.token, onAuthenticated, onSessionExpired);
		return;
	}

	const returningFromOAuth = hasOAuthCallbackInCurrentUrl();
	const result = await initKeycloak();

	if (result.authenticated && result.token) {
		await applyAuthenticatedSession(result.token, onAuthenticated, onSessionExpired);

		if (returningFromOAuth && window.location.pathname.startsWith('/login')) {
			window.history.replaceState({}, '', '/launcher');
		}

		return;
	}

	await onUnauthenticated();

	logOAuthFailure(result.oauthError ?? consumeLastKeycloakOAuthError());
}

export async function loginWithKeycloak(): Promise<void> {
	clearKeycloakSessionStorage();

	if (Capacitor.isNativePlatform()) {
		await loginWithKeycloakNative();
		return;
	}

	await getKeycloak().login({
		redirectUri: `${window.location.origin}/login`,
		scope: getKeycloakDefaultScope(),
	});
}

/**
 * Completes native OTP API login and applies the same session handlers as OAuth bootstrap.
 */
export async function completeOtpApiLogin(challengeId: string, otp: string): Promise<void> {
	if (!authenticatedHandler || !sessionExpiredHandler) {
		throw new Error('Keycloak auth is not bootstrapped');
	}

	clearKeycloakSessionStorage();
	const result = await loginWithOtpApi({ challengeId, otp });

	if (!result.authenticated || !result.token) {
		throw new Error('OTP verification failed');
	}

	await applyAuthenticatedSession(result.token, authenticatedHandler, sessionExpiredHandler);
}
